import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  useKnowledgeSources,
  useKnowledgeSourcesLoading,
  useResearchTypes,
  useTermsAndConditionsState,
} from "../../../adapters/insights-agent/state/hooks/stateHooks.ts";
import { useInsightsAgentService } from "../../../adapters/insights-agent/state/hooks/useInsightsAgentService.tsx";
import { useFeatureFlagValue } from "../../../config/FeatureFlagManager/hooks.ts";
import { WorkflowWizard } from "../../../feature-components/WorkflowWizard/WorkflowWizard.tsx";

export const ConnectedWizard = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const appService = useInsightsAgentService();
  useEffect(() => {
    appService.refreshKnowledgeSources().then();
    appService.refreshResearchTypes().then();
  }, [appService]);
  const knowledgeSources = useKnowledgeSources();
  const researchTypes = useResearchTypes();
  const knowledgeSourcesLoading = useKnowledgeSourcesLoading();
  const knowledgeSourceOptions = useMemo(
    () =>
      knowledgeSources.map((source) => ({
        value: source.name,
        label: source.name,
        repoUrl: source.repoUrl,
      })),
    [knowledgeSources],
  );
  const projectStartHandler = useCallback(
    async (
      source: string,
      brief: string,
      questions: string,
      researchType?: string,
    ) => {
      if (!appService) {
        throw new Error("App service not initialized");
      }
      setLoading(true);
      const { data } = await appService.startMetaAnalysis({
        knowledgeSourceId: source,
        researchGoal: brief,
        additionalQuestions: questions,
        researchType,
      });
      if (!data) throw new Error("No data");
      setLoading(false);
      navigate(`/workflows/${data.id}`);
    },
    [appService, navigate],
  );
  const enableResearchTypes = useFeatureFlagValue("enableResearchTypes");
  const enableResearchTypeDescriptions = useFeatureFlagValue(
    "enableResearchTypeDescriptions",
  );
  const enableIngestionFolderLink = useFeatureFlagValue(
    "enableIngestionFolderLink",
  );
  const termsState = useTermsAndConditionsState();
  const enableTermsAndConditions =
    termsState.status !== "acceptance_not_required";

  const flags = useMemo(
    () => ({
      enableResearchTypes,
      enableResearchTypeDescriptions,
      enableIngestionFolderLink,
      enableTermsAndConditions,
    }),
    [
      enableResearchTypeDescriptions,
      enableResearchTypes,
      enableIngestionFolderLink,
      enableTermsAndConditions,
    ],
  );
  return (
    <WorkflowWizard
      onProjectStart={projectStartHandler}
      knowledgeSources={knowledgeSourceOptions}
      knowledgeSourcesLoading={knowledgeSourcesLoading}
      researchTypes={researchTypes}
      featureFlags={flags}
      loading={loading}
    />
  );
};
